import { useEffect } from 'react';
import { getToolSEO } from '../data/toolSEOData';

// Generate JSON-LD structured data for a tool page
export const generateToolSchema = (tool, seoData) => {
  if (!tool) return null;

  const baseUrl = window.location.origin;
  
  // SoftwareApplication Schema
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": tool.name,
    "description": seoData?.description || tool.description,
    "url": `${baseUrl}/tools/${tool.slug}`,
    "applicationCategory": "WebApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": tool.tier === 'free' ? "0" : tool.tier === 'pro' ? "19" : "49",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "author": {
      "@type": "Organization",
      "name": "ToolsForge"
    }
  };

  // FAQPage Schema
  const faqs = seoData?.faqs || tool.faqs || [];
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tools",
        "item": `${baseUrl}/tools`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": tool.name,
        "item": `${baseUrl}/tools/${tool.slug}`
      }
    ]
  };

  // HowTo Schema for usage steps
  const usageSteps = seoData?.usageSteps || [];
  const howToSchema = usageSteps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Use ${tool.name}`,
    "description": `Step-by-step guide to using the ${tool.name} tool`,
    "step": usageSteps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description
    }))
  } : null;

  return { softwareSchema, faqSchema, breadcrumbSchema, howToSchema };
};

// Component to inject JSON-LD into the page
const StructuredData = ({ tool, seoData }) => {
  useEffect(() => {
    if (!tool) return;

    const schemas = generateToolSchema(tool, seoData);
    if (!schemas) return;

    // Remove existing schema scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new schema scripts
    const addSchema = (schema, id) => {
      if (!schema) return;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    addSchema(schemas.softwareSchema, 'software-schema');
    addSchema(schemas.faqSchema, 'faq-schema');
    addSchema(schemas.breadcrumbSchema, 'breadcrumb-schema');
    addSchema(schemas.howToSchema, 'howto-schema');

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [tool, seoData]);

  return null;
};

// Generate Organization schema for homepage
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "ToolsForge",
  "url": window.location.origin,
  "logo": `${window.location.origin}/logo.png`,
  "description": "Free online tools for developers, designers, and marketers. JSON formatter, password generator, color picker, crypto calculators, and 180+ more utilities.",
  "sameAs": [
    "https://twitter.com/toolsforge",
    "https://github.com/toolsforge"
  ]
});

// Generate WebSite schema
export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "ToolsForge",
  "url": window.location.origin,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${window.location.origin}/tools?search={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
});

// Generate BlogPosting schema
export const generateBlogSchema = (post) => {
  if (!post) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "datePublished": post.created_at,
    "publisher": {
      "@type": "Organization",
      "name": "ToolsForge"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${window.location.origin}/blog/${post.slug}`
    }
  };
};

export default StructuredData;
